import { createListLinkTarget } from '../../utils/listPageState'

const EDIT_BANNER_DRAFT_PREFIX = 'admin:bannerDraft:edit:'
export const NEW_BANNER_DRAFT_KEY = 'admin:bannerDraft:new'
export const BANNER_IMAGE_FILE_ERROR_MESSAGE = 'png, jpg, jpeg 파일만 업로드 가능합니다.'
export const BANNER_IMAGE_FILE_ACCEPT = 'image/png, image/jpeg'
export const BANNER_IMAGE_FIELD_CONFIGS = [
    {
        fieldName: 'image',
        inputId: 'image',
        previewAlt: '메인이미지',
        uploadLabel: '메인이미지 선택',
        imageType: 1,
        topOnly: false,
    },
    {
        fieldName: 'image2',
        inputId: 'image2',
        previewAlt: '보조이미지',
        uploadLabel: '보조이미지 선택',
        imageType: 2,
        topOnly: true,
    },
]

const BANNER_DRAFT_FIELDS = [
    'startTime',
    'endTime',
    'location',
    'title',
    'titleSub',
    'url',
    'status',
    'order',
]

export const createDefaultBannerData = () => ({
    startTime: new Date(),
    endTime: new Date(),
    location: 'top',
    title: '',
    titleSub: '',
    url: '/animalrun',
    status: 1,
    order: 0,
})

const getSessionStorage = () => {
    if (typeof window === 'undefined') {
        return null
    }

    return window.sessionStorage
}

const serializeDateValue = (value) => {
    if (value === null) {
        return null
    }

    if (value === undefined || value === '') {
        return ''
    }

    const nextDate = new Date(value)

    return Number.isNaN(nextDate.getTime()) ? '' : nextDate.toISOString()
}

const parseDateValue = (value, fallbackValue) => {
    if (value === null) {
        return null
    }

    if (value === undefined || value === '') {
        return fallbackValue
    }

    const nextDate = new Date(value)

    return Number.isNaN(nextDate.getTime()) ? fallbackValue : nextDate
}

// 배너 폼에서 실제로 수정하는 값만 sessionStorage에 남긴다.
export const createBannerDraftPayload = ({ data = {} }) => {
    return {
        data: {
            startTime: serializeDateValue(data.startTime),
            endTime: serializeDateValue(data.endTime),
            location: data.location || 'top',
            title: data.title || '',
            titleSub: data.titleSub || '',
            url: data.url || '',
            status: data.status ?? 1,
            order: data.order ?? 0,
        }
    }
}

// 서버 기본값 위에 draft만 덮어써서 새로고침 뒤에도 입력 흐름을 이어간다.
export const applyBannerDraft = ({ baseData = {}, draft }) => {
    if (!draft) {
        return {
            data: baseData,
        }
    }

    const draftData = draft.data || {}
    const nextData = {
        ...baseData,
    }

    BANNER_DRAFT_FIELDS.forEach((field) => {
        if (draftData[field] === undefined) {
            return
        }

        if (field === 'startTime' || field === 'endTime') {
            nextData[field] = parseDateValue(draftData[field], baseData[field] ?? null)
            return
        }

        nextData[field] = draftData[field]
    })

    return {
        data: nextData,
    }
}

export const applyBannerFieldChange = ({ data = {}, name, value }) => {
    if (!name) {
        return data
    }

    return {
        ...data,
        [name]: value,
    }
}

// 등록/수정 화면이 같은 문자열 정리와 FormData 구성을 사용하도록 맞춘다.
export const prepareBannerSubmit = ({
    data = {},
    bannerId,
    requireMainImage = false,
} = {}) => {
    const nextTitle = (data.title || '').trim()
    const nextTitleSub = (data.titleSub || '').trim()
    const nextUrl = (data.url || '').trim()
    const nextData = {
        ...data,
        title: nextTitle,
        titleSub: nextTitleSub,
        url: nextUrl,
    }

    if (!nextTitle) {
        return {
            nextData,
            errorMessage: '제목을 입력하세요.',
        }
    }

    if (requireMainImage && !data.image) {
        return {
            nextData,
            errorMessage: '메인 이미지를 업로드하세요.',
        }
    }

    const formData = new FormData()
    formData.append('image', nextData.image)
    formData.append('image2', nextData.image2)

    if (bannerId !== undefined && bannerId !== null && bannerId !== '') {
        formData.append('bannerId', bannerId)
    }

    formData.append('startTime', nextData.startTime)
    formData.append('endTime', nextData.endTime)
    formData.append('location', nextData.location)
    formData.append('title', nextTitle)
    formData.append('titleSub', nextTitleSub)
    formData.append('url', nextUrl)
    formData.append('status', nextData.status)
    formData.append('order', nextData.order)

    return {
        nextData,
        formData,
        errorMessage: '',
    }
}

export const createBannerListLinkTarget = ({
    listSearch = '',
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => createListLinkTarget({
    pathname: '/banners',
    search: listSearch,
    curPage,
    searchText,
    searchSelect,
    ...(snackbarState ? {
        openSnackbar: true,
        severitySnackbar: snackbarState.severity,
        messageSnackbar: snackbarState.message,
    } : {}),
})

// 저장/취소 후 복귀 시 기존 배너 목록 필터와 페이지를 유지하는 규칙을 한 곳에서 맞춘다.
export const moveToBannerList = ({
    draftKey,
    clearDraft,
    setNavigationUnlocked,
    history,
    listSearch,
    snackbarState,
    curPage,
    searchText,
    searchSelect,
}) => {
    setNavigationUnlocked(true)
    clearDraft(draftKey)

    setTimeout(() => {
        history.push(createBannerListLinkTarget({
            listSearch,
            snackbarState,
            curPage,
            searchText,
            searchSelect,
        }))
    }, 0)
}

export const handleBannerBackNavigation = ({
    event,
    leaveGuardEnabled,
    confirmLeave,
    moveToList,
}) => {
    if (!leaveGuardEnabled) {
        moveToList()
        return
    }

    event.preventDefault()

    if (!confirmLeave()) {
        return
    }

    moveToList()
}

const isBannerImageFile = (file) => {
    return Boolean(file) && (
        (file.type && ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) ||
        /\.(png|jpe?g)$/i.test(file.name || '')
    )
}

export const handleBannerImageUpload = ({
    event,
    imageType,
    setData,
    setMainImage,
    setMainImage2,
    onInvalidFile,
}) => {
    event.preventDefault()

    const input = event.target
    const file = input.files?.[0]

    if (!file) {
        return
    }

    if (!isBannerImageFile(file)) {
        input.value = ''
        onInvalidFile(BANNER_IMAGE_FILE_ERROR_MESSAGE)
        return
    }

    const reader = new FileReader()

    reader.onloadend = () => {
        setData((prev) => ({
            ...prev,
            [input.name]: file,
        }))

        if (imageType === 1) {
            setMainImage(reader.result)
            return
        }

        if (imageType === 2) {
            setMainImage2(reader.result)
        }
    }

    reader.readAsDataURL(file)
}

export const createEditBannerDraftKey = (bannerId) => `${EDIT_BANNER_DRAFT_PREFIX}${bannerId}`

export const readBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return null
    }

    try {
        const storedValue = storage.getItem(draftKey)
        return storedValue ? JSON.parse(storedValue) : null
    } catch (_error) {
        return null
    }
}

export const saveBannerDraft = (draftKey, draftValue) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.setItem(draftKey, JSON.stringify(draftValue))
    } catch (_error) {
        // sessionStorage가 막혀 있어도 입력 흐름은 그대로 유지한다.
    }
}

export const clearBannerDraft = (draftKey) => {
    const storage = getSessionStorage()

    if (!storage || !draftKey) {
        return
    }

    try {
        storage.removeItem(draftKey)
    } catch (_error) {
        // storage 정리에 실패해도 화면 동작은 계속 진행한다.
    }
}
